import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type { BulkResponseConsolidada, Protocolo } from '../types';
import { getFalloMessage, isReintentable, sortFallidosPorSeveridad } from '../utils/bulkErrorMessages';

interface BulkFailuresDialogProps {
  open: boolean;
  result: BulkResponseConsolidada;
  protocolosByUid: Map<string, Protocolo>;
  getEstablecimientoName: (uid: string) => string;
  onClose: () => void;
  onRetry: (uids: string[]) => void;
}

export function BulkFailuresDialog({
  open,
  result,
  protocolosByUid,
  getEstablecimientoName,
  onClose,
  onRetry,
}: BulkFailuresDialogProps): JSX.Element {
  const { exitosos, fallidos } = result;
  const fallidosOrdenados = sortFallidosPorSeveridad(fallidos);
  const reintentables = fallidos.filter(isReintentable).map((f) => f.uid_protocolo);

  const title = exitosos.length === 0 ? 'La operación tuvo errores' : 'La operación se completó parcialmente';

  function handleRetry(): void {
    onRetry(reintentables);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {exitosos.length > 0 && (
            <Typography variant="body2" color="success.main" fontWeight={500}>
              Exitosos: {exitosos.length} {exitosos.length === 1 ? 'protocolo' : 'protocolos'}
            </Typography>
          )}
          <Typography variant="body2" color="error.main" fontWeight={500}>
            Fallaron: {fallidos.length} {fallidos.length === 1 ? 'protocolo' : 'protocolos'}
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Protocolos que no se procesaron:
        </Typography>

        <List dense disablePadding sx={{ mt: 0.5 }}>
          {fallidosOrdenados.map((fallo) => {
            const protocolo = protocolosByUid.get(fallo.uid_protocolo);
            const encabezado = protocolo
              ? `N° ${protocolo.numero_protocolo}${
                  getEstablecimientoName(protocolo.uid_establecimiento)
                    ? ` · ${getEstablecimientoName(protocolo.uid_establecimiento)}`
                    : ''
                }`
              : `Protocolo ${fallo.uid_protocolo.slice(0, 8)}…`;

            return (
              <ListItem key={fallo.uid_protocolo} disableGutters sx={{ py: 0.5 }}>
                <Paper variant="outlined" sx={{ p: 1.25, width: '100%' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {encabezado}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Razón: {getFalloMessage(fallo)}
                  </Typography>
                </Paper>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        {reintentables.length > 0 && (
          <Button onClick={handleRetry} variant="contained" color="primary">
            Reintentar los fallidos
          </Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
